import {
  cashConversionCycleMolecule,
  changeInNetWorkingCapitalMolecule,
  changeInNetWorkingCapitalPercentMolecule,
  costOfGoodsSoldItemization,
  currentAssetsItemization,
  currentLiabilitiesItemization,
  currentRatioMolecule,
  daysInventoryOutstandingMolecule,
  daysPayableOutstandingMolecule,
  daysSalesOutstandingMolecule,
  fixedOpExAsPercentOfRevenueMolecule,
  fixedOperatingExpensesItemization,
  grossMarginsMolecule,
  grossProfitMolecule,
  grossRevenueItemization,
  netWorkingCapitalMolecule,
  nwcAsPercentOfRevenueMolecule,
  periodFormatIdAtom,
  quickRatioMolecule,
  workingCapitalEpochsAtomsContainer,
  workingCapitalFormulaAtom,
} from "@/financeModels/owasco/workingCapital";
import { formulaRangeAtom } from "@/financeModels/owasco/workingCapitalEpoch";
import { Atom, Getter, atom } from "jotai";
import { CellTypes } from "../types";
import {
  ItemizationAtom,
  boldStyle,
  createEmptyRow,
  createGroupEndRow,
  createGroupStartRow,
  createTitleRow,
  groupWorksheetRows,
  itemizationToRows,
  moleculeToRow,
  toSpreadsheetCell,
} from "./helper";

const XLSXStub = () => import("xlsx-js-style");
type XLSXModule = Awaited<ReturnType<typeof XLSXStub>>;

const { orderedEpochsMolecule } = workingCapitalEpochsAtomsContainer;

const formatDate = (value: number) =>
  new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const formatFormulaRange = (timestamps: number[]) => {
  if (!timestamps.length) return "";
  if (timestamps.length === 1) return formatDate(timestamps[0]);
  return `${formatDate(timestamps[0])} - ${formatDate(
    timestamps[timestamps.length - 1],
  )}`;
};

const dayCell = (value: unknown) => {
  const numericValue = Number(value);
  if (value === null || value === undefined || Number.isNaN(numericValue)) {
    return {
      v: "",
      t: "s",
      z: undefined,
    };
  }

  return {
    v: Number(numericValue.toFixed(2)),
    t: "n",
    z: "0.00",
  };
};

const dayMoleculeToRow = (
  get: Getter,
  molecule: Atom<Atom<CellTypes>[]>,
  label: string,
) => {
  const values = get(molecule).map((cellAtom) => {
    const cell = get(cellAtom);
    if (typeof cell?.value === "string") {
      return toSpreadsheetCell(get, cell.value, { type: "string" });
    }
    return dayCell(cell?.value);
  });

  return [
    toSpreadsheetCell(get, label, { type: "string", style: boldStyle }),
    ...values,
  ];
};

const groupedItemizationWithLineItems = (
  get: Getter,
  label: string,
  itemizationAtom: ItemizationAtom,
  options?: { isNegative?: boolean },
) => {
  const baseRows = itemizationToRows(get, label, itemizationAtom, options);
  const itemization = get(itemizationAtom);
  const isActive = get(itemization.activeAtom);

  if (isActive) {
    return baseRows;
  }

  const lineItemRows = itemization.lineItems.map((lineItem, index) =>
    moleculeToRow(get, itemization.lineItemsMolecules[index], {
      ...options,
      label: lineItem.label,
    }),
  );

  if (!lineItemRows.length) {
    return baseRows;
  }

  return [
    ...baseRows,
    createGroupStartRow(),
    ...lineItemRows,
    createGroupEndRow(),
  ];
};

export const buildWorkingCapitalWorksheet = (get: Getter, XLSX: XLSXModule) => {
  const pageTitle = (window.document.title || "ETA IQ Working Capital")
    .replace("Model (", "")
    .replace(")", "");

  const workingCapitalFormula = get(workingCapitalFormulaAtom);
  const formulaRange = get(formulaRangeAtom);
  const epochs = get(orderedEpochsMolecule).map((epochAtom) => get(epochAtom));
  const periodFormatId = get(periodFormatIdAtom);

  const rows = [
    [pageTitle],
    ["Created on - " + new Date().toLocaleString()],
    createEmptyRow(),
    [
      undefined,
      `${workingCapitalFormula.label} (${periodFormatId})`,
      ...epochs.map((epoch) => formatDate(epoch.date)),
    ],
    [undefined, formatFormulaRange(formulaRange)],
    createEmptyRow(),

    createTitleRow("COMPANY FINANCIALS"),
    createEmptyRow(),
    ...itemizationToRows(get, "Gross Revenue", grossRevenueItemization),
    ...itemizationToRows(
      get,
      "Cost of Goods Sold (COGS)",
      costOfGoodsSoldItemization,
      { isNegative: true },
    ),
    moleculeToRow(get, grossProfitMolecule, {
      label: "Gross Profit",
      style: boldStyle,
    }),
    moleculeToRow(get, grossMarginsMolecule, {
      label: "Gross Margins (%)",
      type: "percent",
    }),
    ...groupedItemizationWithLineItems(
      get,
      "Fixed Operating Expenses",
      fixedOperatingExpensesItemization,
      { isNegative: true },
    ),
    moleculeToRow(get, fixedOpExAsPercentOfRevenueMolecule, {
      label: "Fixed OpEx as Percent of Revenue (%)",
      type: "percent",
    }),

    createEmptyRow(),
    createTitleRow("CURRENT ASSETS & LIABILITIES"),
    createEmptyRow(),
    ...groupedItemizationWithLineItems(
      get,
      "Current Assets",
      currentAssetsItemization,
    ),
    ...itemizationToRows(
      get,
      "Current Liabilities",
      currentLiabilitiesItemization,
    ),
    moleculeToRow(get, netWorkingCapitalMolecule, {
      label: "Net Working Capital (NWC)",
      style: boldStyle,
    }),
    moleculeToRow(get, changeInNetWorkingCapitalMolecule, {
      label: "Change in NWC Amount",
    }),
    moleculeToRow(get, changeInNetWorkingCapitalPercentMolecule, {
      label: "Change in NWC Percent",
      type: "percent",
    }),

    createEmptyRow(),
    createTitleRow("LIQUIDITY & SOLVENCY METRICS"),
    createEmptyRow(),
    moleculeToRow(get, nwcAsPercentOfRevenueMolecule, {
      label: "NWC as Percent of Revenue",
      type: "percent",
      style: boldStyle,
    }),
    moleculeToRow(get, quickRatioMolecule, {
      label: "Quick Ratio",
      type: "dscr",
      style: boldStyle,
    }),
    moleculeToRow(get, currentRatioMolecule, {
      label: "Current Ratio",
      type: "dscr",
      style: boldStyle,
    }),

    createEmptyRow(),
    createTitleRow("WORKING CAPITAL EFFICIENCY METRICS"),
    createEmptyRow(),
    dayMoleculeToRow(
      get,
      daysSalesOutstandingMolecule,
      "Days Sales Outstanding (DSO)",
    ),
    dayMoleculeToRow(
      get,
      daysInventoryOutstandingMolecule,
      "Days Inventory Outstanding (DIO)",
    ),
    dayMoleculeToRow(
      get,
      daysPayableOutstandingMolecule,
      "Days Payables Outstanding (DPO)",
    ),
    dayMoleculeToRow(
      get,
      cashConversionCycleMolecule,
      "Cash Conversion Cycle (CCC)",
    ),
  ];

  const rowGroups: { start: number; end: number }[] = [];
  const cleanedRows: typeof rows = [];
  let currentGroupStartIndex: number | null = null;

  rows.forEach((row) => {
    if (row[0] === "GROUP_START") {
      currentGroupStartIndex = cleanedRows.length;
    } else if (row[0] === "GROUP_END") {
      if (currentGroupStartIndex !== null) {
        rowGroups.push({
          start: currentGroupStartIndex,
          end: cleanedRows.length - 1,
        });
        currentGroupStartIndex = null;
      }
    } else {
      cleanedRows.push(row);
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(cleanedRows);

  const COL_WIDTH = 270;
  const COL_INDEX = 0;
  if (!ws["!cols"]) ws["!cols"] = [];
  if (!ws["!cols"][COL_INDEX]) ws["!cols"][COL_INDEX] = { wch: 8 };
  ws["!cols"][COL_INDEX].wpx = COL_WIDTH;

  rowGroups.forEach(({ start, end }) => groupWorksheetRows(ws, start, end));

  return ws;
};

export const workingCapitalExportAtom = atom(null, async (get) => {
  const XLSX = await XLSXStub();
  const ws = buildWorkingCapitalWorksheet(get, XLSX);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Working Capital");

  const fmt = "xlsx";
  const filename = `working-capital-${new Date()
    .toISOString()
    .replace(/:/g, "-")
    .replace(/\..+/, "")}.${fmt}`;

  XLSX.writeFile(wb, filename);

  return;
});
